// src/utils/errorUtils.ts

export enum ErrorType {
    NETWORK = 'network',
    VALIDATION = 'validation',
    SERVER = 'server',
    FILE = 'file',
    RUNTIME = 'runtime',
    UNKNOWN = 'unknown'
}

// Keeping the original object for backward compatibility if needed, 
// but using the Enum is preferred in TS.
export const ErrorTypes = ErrorType;

export interface ErrorDetails {
    type: ErrorType;
    message: string;
    details?: string;
    stack?: string;
    timestamp?: string;
    url?: string;
    componentStack?: string;
}

export const logError = (error: any, errorInfo: { componentStack?: string } = {}): void => {
    const errorDetails: ErrorDetails = {
        type: error?.type || ErrorType.UNKNOWN,
        message: error?.message || 'Unknown error',
        stack: error?.stack,
        componentStack: errorInfo?.componentStack,
        timestamp: new Date().toISOString(),
        url: typeof window !== 'undefined' ? window.location.href : '',
    };

    if (import.meta.env.MODE === 'development') {
        console.error('Error caught by boundary:', errorDetails);
    }
};

export const getErrorDetails = (error: any, response: Response | null = null): ErrorDetails => {
    // Network connectivity issues
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
        return {
            type: ErrorType.NETWORK,
            message: 'Please check your internet connection',
            details: error.message
        };
    }

    // Request timeout
    if (error.name === 'AbortError') {
        return {
            type: ErrorType.NETWORK,
            message: 'Request timed out. Please try again',
            details: 'Operation took too long to complete'
        };
    }

    // File-related errors
    if (error.name === 'FileError') {
        return {
            type: ErrorType.FILE,
            message: 'Failed to read file',
            details: error.message
        };
    }

    // Server response errors
    if (response) {
        switch (response.status) {
            case 413:
                return {
                    type: ErrorType.VALIDATION,
                    message: 'File size too large',
                    details: `Maximum size: ${formatFileSize(response.headers.get('max-size') || '1MB')}`
                };
            case 400:
                return {
                    type: ErrorType.VALIDATION,
                    message: 'Invalid input. Please check your text and try again',
                    details: error.message
                };
            case 429:
                return {
                    type: ErrorType.SERVER,
                    message: 'Too many requests. Please wait a moment and try again',
                    details: 'Rate limit exceeded'
                };
            case 500:
                return {
                    type: ErrorType.SERVER,
                    message: 'Server error. Please try again later',
                    details: error.message
                };
            default:
                return {
                    type: ErrorType.SERVER,
                    message: 'An unexpected error occurred',
                    details: error.message
                };
        }
    }

    // Default unknown error
    return {
        type: ErrorType.UNKNOWN,
        message: 'Something went wrong. Please try again',
        details: error.message
    };
};

export const formatFileSize = (size: number | string): string => {
    if (typeof size === 'string') return size;
    const units = ['B', 'KB', 'MB', 'GB'];
    let formattedSize = size;
    let unitIndex = 0;

    while (formattedSize >= 1024 && unitIndex < units.length - 1) {
        formattedSize /= 1024;
        unitIndex++;
    }

    return `${Math.round(formattedSize * 100) / 100}${units[unitIndex]}`;
};
